import { useState, useContext, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaComment, FaPhone } from 'react-icons/fa';
import DriverMasterLayout from "../../../layouts/Drivers/DriverMasterLayout";
import '../../../assets/css/Drivers/DriverActiveRides/RideOnWay.css';
import axios from "axios";
import AppContext from "../../../context/AppContext";

const RideOnWay = () => {
    const navigate = useNavigate();
    const { baseUrl, authToken, selectedRideforFindingDriver, GOOGLE_API_KEY } = useContext(AppContext);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [mapLoading, setMapLoading] = useState(true);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);

    const handleBack = () => {
        navigate(-1);
    };

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setCurrentLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                (error) => {
                    console.error('Error getting location:', error);
                    setCurrentLocation(null);
                }
            );
        }
    }, []);

    // ===== GOOGLE MAP =====
    useEffect(() => {
        const loadGoogle = () =>
            new Promise((resolve) => {
                if (window.google) { resolve(window.google); return; }
                const script = document.createElement("script");
                script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}`;
                script.onload = () => resolve(window.google);
                script.onerror = () => {
                    console.warn("⚠️ Google Maps API failed to load");
                    resolve(null);
                };
                document.head.appendChild(script);
            });

        loadGoogle().then((google) => {
            if (!mapRef.current || !google) {
                setMapLoading(false);
                return;
            }

            const dropLat = parseFloat(selectedRideforFindingDriver?.dropoffLat);
            const dropLng = parseFloat(selectedRideforFindingDriver?.dropoffLng);
            const hasDropoff = !isNaN(dropLat) && !isNaN(dropLng);
            const center = hasDropoff ? { lat: dropLat, lng: dropLng } : { lat: 31.5204, lng: 74.3587 };

            if (!hasDropoff) {
                console.warn("⚠️ dropoffLat/dropoffLng missing in ride data, showing default location");
            }

            const map = new google.maps.Map(mapRef.current, {
                center,
                zoom: 14,
                disableDefaultUI: true,
                zoomControl: true,
                gestureHandling: 'greedy',
            });

            mapInstanceRef.current = map;
            setMapLoading(false);

            if (hasDropoff) {
                new google.maps.Marker({
                    position: { lat: dropLat, lng: dropLng },
                    map,
                    title: selectedRideforFindingDriver?.dropofflocation || "Drop-off location",
                    icon: {
                        url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                    },
                });
            }

            if (currentLocation) {
                new google.maps.Marker({
                    position: currentLocation,
                    map,
                    title: "Your Location",
                    icon: {
                        url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
                    },
                });

                if (hasDropoff) {
                    const bounds = new google.maps.LatLngBounds();
                    bounds.extend(currentLocation);
                    bounds.extend({ lat: dropLat, lng: dropLng });
                    map.fitBounds(bounds);
                }
            }
        });

        return () => {
            mapInstanceRef.current = null;
        };
    }, [selectedRideforFindingDriver, currentLocation, GOOGLE_API_KEY]);

    const handleCall = () => {
        window.open(`tel:${selectedRideforFindingDriver?.tdcustomer?.phone}`);
    };

    const handleMessage = () => {
        console.log('Messaging customer...');
        navigate('/Driver-chat');
    };

    const handleCompleteRide = () => {
        const formData = new FormData();
        formData.append("tdride_id", selectedRideforFindingDriver?.id);

        axios.post(`${baseUrl}/driver/ride/completeRide`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${authToken}`,
            },
        }).then((response) => {
            if (response.data.success === true) {
                navigate('/ride-completed');
            }
        }).catch((error) => {
            console.log(error);
            alert("Network Error! Please try again.");
        });
    };

    return (
        <DriverMasterLayout showHeader={false}>
        <div className="ride-onway">
            {/* Back Button */}
            <Button className="ride-onway-back-btn" onClick={handleBack}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M19 12H5M12 19l-7-7 7-7" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </Button>

            {/* Google Maps Container */}
            <div className="ride-onway__map-container">
                {mapLoading && (
                    <div className="ride-onway__map-loading">
                        <p>Loading map...</p>
                    </div>
                )}
                <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
            </div>

            {/* Customer Info Card */}
            <div className="ride-onway__customer-card">
                <div className="ride-onway__customer-info">
                    <div className="ride-onway__customer-avatar">
                        {selectedRideforFindingDriver?.customer_image ? (
                            <img src={selectedRideforFindingDriver?.customer_image} alt={selectedRideforFindingDriver?.tdcustomer?.customerName} />
                        ) : (
                            <span>{selectedRideforFindingDriver?.tdcustomer?.name?.charAt(0)}</span>
                        )}
                    </div>
                    <div className="ride-onway__customer-details">
                        <h6 className="ride-onway__customer-name">{selectedRideforFindingDriver?.tdcustomer?.customerName}</h6>
                        <div className="ride-onway__customer-rating">
                            ★ {selectedRideforFindingDriver?.rating}
                        </div>
                    </div>
                    <div className="ride-onway__action-buttons">
                        <button
                            className="ride-onway__action-btn ride-onway__action-btn--call"
                            onClick={handleCall}
                        >
                            <FaPhone size={16} />
                        </button>
                        <button
                            className="ride-onway__action-btn ride-onway__action-btn--message"
                            onClick={handleMessage}
                        >
                            <FaComment size={16} />
                        </button>
                    </div>
                </div>

                {/* Complete Ride Button */}
                <button
                    className="ride-onway__complete-btn"
                    onClick={handleCompleteRide}
                >
                    Complete Ride
                </button>
            </div>
        </div>
        </DriverMasterLayout>
    );
};

export default RideOnWay;